/**
 * coupon-offers-core.js
 *
 * Pure logic for the "Available coupons" widgets (PDP, basket, cart drawer). No DOM,
 * no fetch: coupon-offers.js does the I/O and test/coupon-offers-core.test.js covers
 * this file in Node.
 *
 * Shopify's /cart/update.js takes the full list of codes in its `discount` parameter
 * and replaces whatever was on the cart. So applying one code means sending the
 * existing codes plus the new one, and removing one means sending the rest.
 */
(function (global) {
  'use strict';

  function normaliseCode(code) {
    return String(code == null ? '' : code)
      .trim()
      .toUpperCase();
  }

  /** Codes currently on the cart, from a /cart.js or /cart/update.js response. */
  function cartCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return [];
    return cart.discount_codes.map((d) => d.code).filter(Boolean);
  }

  /** Existing codes plus `code`, without a case-insensitive duplicate. */
  function mergeCodes(existing, code) {
    const wanted = normaliseCode(code);
    const list = (existing || []).filter(Boolean);
    if (!wanted || list.some((c) => normaliseCode(c) === wanted)) return list.slice();
    return list.concat(String(code).trim());
  }

  /** Existing codes without `code` (case-insensitive). */
  function removeCode(existing, code) {
    const unwanted = normaliseCode(code);
    return (existing || []).filter((c) => c && normaliseCode(c) !== unwanted);
  }

  /** The cart's entry for `code`, or null. Entries look like { code, applicable }. */
  function findCode(cart, code) {
    const wanted = normaliseCode(code);
    if (!cart || !Array.isArray(cart.discount_codes)) return null;
    return cart.discount_codes.find((d) => normaliseCode(d.code) === wanted) || null;
  }

  function cartHasProduct(cart, productId) {
    if (!cart || !Array.isArray(cart.items) || productId == null) return false;
    return cart.items.some((item) => String(item.product_id) === String(productId));
  }

  /**
   * What happened after asking Shopify to add `code`.
   *
   * - 'applied':  on the cart and discounting it
   * - 'pending':  on the cart, not discounting yet because the product it was applied
   *               from is not in the basket. It starts working once it is added.
   * - 'deferred': applied from a product page but Shopify did not keep it (e.g. an
   *               empty cart). The caller stores it and retries after add to cart.
   * - 'rejected': does not apply to this basket (wrong items, minimum spend not met,
   *               cannot combine with another code, expired, unknown code)
   */
  function classifyApplyResult(cart, code, options) {
    const opts = options || {};
    const entry = findCode(cart, code);
    const fromProduct = opts.context === 'product';
    const productInCart = fromProduct && cartHasProduct(cart, opts.productId);

    if (entry && entry.applicable !== false) return 'applied';
    if (entry) return fromProduct && !productInCart ? 'pending' : 'rejected';
    return fromProduct && !productInCart ? 'deferred' : 'rejected';
  }

  /**
   * Estimated saving in minor units (pence) on one unit priced `priceCents`.
   * type: 'percentage' | 'fixed_item' | 'fixed_order'. value: percent, or major units.
   * Never more than the price.
   */
  function computeSaving(type, value, priceCents) {
    const price = Math.max(0, Math.round(Number(priceCents) || 0));
    const amount = Number(value) || 0;
    if (amount <= 0 || price === 0) return 0;

    const saving = type === 'percentage' ? Math.round((price * amount) / 100) : Math.round(amount * 100);
    return Math.min(saving, price);
  }

  var api = {
    normaliseCode: normaliseCode,
    cartCodes: cartCodes,
    mergeCodes: mergeCodes,
    removeCode: removeCode,
    findCode: findCode,
    cartHasProduct: cartHasProduct,
    classifyApplyResult: classifyApplyResult,
    computeSaving: computeSaving,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (global) {
    global.CouponOffersCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : this);
